import { Parser, ENode } from "./ast";
import type {
  Operator,
  FilterItem,
  FilterSchema,
  FilterBuilderSchema,
  FilterBuilderField,
} from "./types";

function parseExpression(expression: string) {
  const parser = new Parser(expression);
  return parser.parse();
}

export function buildFilterSchema(
  ast: ENode,
  fields: FilterBuilderField[]
): FilterItem | null {
  if (ast.node.value === "and" || ast.node.value === "or") {
    if (!ast.left || !ast.right) {
      throw new Error(`Invalid expression ${ast.node.value}`);
    }

    const filters: FilterItem[] = [
      buildFilterSchema(ast.left, fields),
      buildFilterSchema(ast.right, fields),
    ].filter((x) => x !== null) as FilterItem[];

    if (filters.length === 0) {
      return null;
    }
    if (filters.length === 1) {
      return filters[0];
    }
    return {
      logic: ast.node.value.toLowerCase() as "and" | "or",
      filters,
    };
  }

  const field = fields.find((f) => f.column === ast.node.value);
  if (!field) {
    return null;
  }
  return {
    column: field.column,
    operator: field.operator.toLowerCase() as Operator,
    value: field.value,
  };
}

export function buildFilter(
  schema: FilterBuilderSchema,
  defaultLogic = "and"
): FilterSchema | null {
  const { expression, fields } = schema;
  if (!fields || fields.length === 0) {
    return null;
  }
  if (!expression) {
    return {
      logic: defaultLogic as "and" | "or",
      filters: fields,
    };
  }
  // Expression is a string with column names surrounded by parenthesis
  // and interleaved with logical operators AND and OR
  // we build an AST to parse the expression
  const ast = parseExpression(expression);
  // we use the AST to build a FilterSchema substituting the column names
  // with the columns from the fields
  const filters = buildFilterSchema(ast, fields);
  if (!filters) {
    return null;
  }
  if (typeof filters !== "object" || !("logic" in filters)) {
    return {
      logic: defaultLogic as "and" | "or",
      filters: [filters],
    };
  }
  return filters;
}
